import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Investors from "./pages/Investors";
import Manage from "./pages/Manage";
import Sales from "./pages/Sales";
import CRM from "./pages/CRM";
import Marketing from "./pages/Marketing";
import AP from "./pages/AP";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import OnboardPublic from "./pages/OnboardPublic";
import Budget from "./pages/Budget";
import Reports from "./pages/Reports";
import ReportEditor from "./pages/ReportEditor";
import ReportPublic from "./pages/ReportPublic";
import Proposals from "./pages/Proposals";
import ProposalEditor from "./pages/ProposalEditor";
import Guide from "./pages/Guide";
import Bookings from "./pages/Bookings";
import Availability from "./pages/Availability";
import Settings from "./pages/Settings";
import CheckInHistory from "./pages/CheckInHistory";
import Ramp from "./pages/Ramp";
import ProposalPublic from "./pages/ProposalPublic";
import Locations from "./pages/Locations";
import LocationDetail from "./pages/LocationDetail";
import Cameras from "./pages/Cameras";
import Boards from "./pages/Boards";
import Doors from "./pages/Doors";
import Music from "./pages/Music";
import Members from "./pages/Members";
import Executive from "./pages/Executive";
import Social from "./pages/Social";
import Newsletters from "./pages/Newsletters";
import Programming from "./pages/Programming";

export const landingFor = () => "/home";

function Protected({ children, allow }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-page">
        <div className="h-10 w-10 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (allow && user?.role && !allow.includes(user.role)) return <Navigate to={landingFor(user.role)} replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  // Remount the page when ?location= changes so location-aware pages pick up the new filter.
  const locKey = new URLSearchParams(useLocation().search).get("location") || "";
  return (
    <Routes key={locKey}>
      <Route path="/login" element={user ? <Navigate to={landingFor(user.role)} replace /> : <Login />} />
      <Route path="/social" element={<Protected allow={["admin","regional","community"]}><Social /></Protected>} />
      <Route path="/newsletters" element={<Protected allow={["admin","regional","community"]}><Newsletters /></Protected>} />
      <Route path="/programming" element={<Protected allow={["admin","regional","community"]}><Programming /></Protected>} />
      <Route path="/executive" element={<Protected allow={["admin"]}><Executive /></Protected>} />
      <Route path="/r/:token" element={<ReportPublic />} />
      <Route path="/onboard/:token" element={<OnboardPublic />} />
      <Route path="/home" element={<Protected><Home /></Protected>} />
      <Route path="/onboarding" element={<Protected allow={["admin","regional","community"]}><Onboarding /></Protected>} />
      <Route path="/p/:token" element={<ProposalPublic />} />
      <Route path="/locations" element={<Protected allow={["admin","regional","investor","community"]}><Locations /></Protected>} />
      <Route path="/locations/:loc" element={<Protected allow={["admin","regional","investor","community"]}><LocationDetail /></Protected>} />
      <Route path="/" element={<Protected allow={["admin","regional","investor"]}><Dashboard /></Protected>} />
      <Route path="/investors" element={<Protected allow={["admin"]}><Investors /></Protected>} />
      <Route path="/manage" element={<Protected allow={["admin"]}><Manage /></Protected>} />
      <Route path="/sales" element={<Protected allow={["admin","regional","investor","community"]}><Sales /></Protected>} />
      <Route path="/crm" element={<Protected allow={["admin","regional","community"]}><CRM /></Protected>} />
      <Route path="/marketing" element={<Protected allow={["admin","regional"]}><Marketing /></Protected>} />
      <Route path="/ap" element={<Protected allow={["admin","finance"]}><AP /></Protected>} />
      <Route path="/budget" element={<Protected allow={["admin","regional"]}><Budget /></Protected>} />
      <Route path="/reports" element={<Protected allow={["admin","regional"]}><Reports /></Protected>} />
      <Route path="/reports/:id" element={<Protected allow={["admin","regional"]}><ReportEditor /></Protected>} />
      <Route path="/proposals" element={<Protected allow={["admin","regional","community"]}><Proposals /></Protected>} />
      <Route path="/proposals/:id" element={<Protected allow={["admin","regional","community"]}><ProposalEditor /></Protected>} />
      <Route path="/members" element={<Protected allow={["admin","regional","community"]}><Members /></Protected>} />
      <Route path="/bookings" element={<Protected allow={["admin","regional","community"]}><Bookings /></Protected>} />
      <Route path="/availability" element={<Protected allow={["admin","regional","community"]}><Availability /></Protected>} />
      <Route path="/checkin-history" element={<Protected allow={["admin","regional","community"]}><CheckInHistory /></Protected>} />
      <Route path="/ramp" element={<Protected allow={["admin","regional","community"]}><Ramp /></Protected>} />
      <Route path="/cameras" element={<Protected allow={["admin","regional","community"]}><Cameras /></Protected>} />
      <Route path="/boards" element={<Protected allow={["admin","regional","community"]}><Boards /></Protected>} />
      <Route path="/doors" element={<Protected allow={["admin","regional","community"]}><Doors /></Protected>} />
      <Route path="/music" element={<Protected allow={["admin","regional","community"]}><Music /></Protected>} />
      <Route path="/settings" element={<Protected allow={["admin","regional","community"]}><Settings /></Protected>} />
      <Route path="/guide" element={<Protected><Guide /></Protected>} />
      <Route path="*" element={<Navigate to={landingFor(user?.role)} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App grain-overlay">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
